import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { IDocumentData } from '@app/transaction-builder/model/document-data.model';
import {
  DocumentViewerComponent,
  defocusInitiatorAfterClosed,
  DialogService,
} from 'ngx-mat-esign360';
import {
  IClientSignField,
  ISignFieldRectangle,
} from '@app/transaction-builder/model/sign-field.model';
import { ISignFieldEditorInputData } from '@app/transaction-builder/model/sign-field-editor.model';
import { SignersService } from '@app/transaction-builder/services/signers.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { v4 as uuidv4 } from 'uuid';
import { ITxFormClientData } from '@app/transaction-builder/model/transaction-form-data.model';
import { TranslateService } from '@ngx-translate/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ProfileService } from '@app/core/services/profile.service';
import {
  IChangeSignFieldPriorityEvent,
  IChangeSignFieldTypeEvent,
} from '../../model/sign-field-events.model';

@Component({
  templateUrl: './document-editor.page.html',
  styleUrls: ['./document-editor.page.scss'],
})
export class DocumentEditorPage implements OnInit {
  @ViewChild(DocumentViewerComponent) documentViewerRef: DocumentViewerComponent;
  signers: ITxFormClientData[];
  activeSigner: ITxFormClientData;
  signFields: IClientSignField[];
  activeSignField: IClientSignField;
  documentData: IDocumentData;
  isDrawing = false;

  get showSignFieldPriority(): boolean {
    return this.profileService.getCurrentProfile()?.canChangeSignFieldPriority;
  }

  constructor(
    private signersService: SignersService,
    @Inject(MAT_DIALOG_DATA) public data: ISignFieldEditorInputData,
    public dialogRef: MatDialogRef<DocumentEditorPage>,
    private dialogService: DialogService,
    private translate: TranslateService,
    public profileService: ProfileService
  ) {}

  ngOnInit(): void {
    this.documentData = this.data.documentData;
    this.signFields = this.data.transactionData.signFieldsByDocument[this.documentData.cid];

    if (!this.documentData || !this.signFields) {
      throw new Error('No document was selected to be edited!');
    }

    this.initSigners();
    this.activateSigner(this.signers.find((signer) => signer.cid === this.data.initialSignerCid));

    defocusInitiatorAfterClosed(this.dialogRef);
  }

  initSigners() {
    this.signers = this.signersService.extractDocSignersFromTxData(
      this.data.transactionData,
      this.documentData.cid
    );

    if (!this.signers.length) {
      throw new Error('No one was selected to sign the document!');
    }

    this.activateSigner(this.signers[0]);
  }

  onSignerClick(signer: ITxFormClientData) {
    if (this.activeSigner?.personalId !== signer.personalId) {
      this.deactivateSignField();
    }
    this.activateSigner(signer);
  }

  onSignFieldItemClicked(signField: IClientSignField) {
    this.activateSignField(signField);
    this.activateSigner(this.signers.find((signer) => signer.cid === signField.client.cid));
  }

  activateSigner(signer: ITxFormClientData) {
    this.activeSigner = signer;
  }

  activateSignField(signField: IClientSignField) {
    this.activeSignField = signField;
  }

  deactivateSignField() {
    this.activeSignField = null;
  }

  onDrawingStarted() {
    this.isDrawing = true;
    /* this.signFields = this.signFields.filter(
      (signField) => signField.client.personalId !== this.activeSigner.personalId
    ); */
  }

  onDrawingFinished(signFieldRectangle: ISignFieldRectangle, pageIndex: number) {
    const newSignField: IClientSignField = {
      ...signFieldRectangle,
      id: uuidv4(),
      client: {
        cid: this.activeSigner.cid,
      },
      pageIndex,
      // type: SignatureType.Hand,
      type: this.data.transactionData.defaultSignatureType,
      priority: 1,
    };
    this.signFields = this.signFields.concat([newSignField]);
    this.isDrawing = false;
    this.activateSignField(newSignField);
  }

  getCurrentSignerSignField(): IClientSignField {
    return this.signFields.find((signField) => signField.client.cid === this.activeSigner.cid);
  }

  onCloseEditor() {
    this.dialogRef.close();
  }

  onSaveSignFields() {
    this.dialogRef.close(this.signFields);
  }

  onDeleteSignField(signField: IClientSignField) {
    this.dialogService
      .openConfirmDialog({
        message: this.translate.instant(marker('DOCUMENT_EDITOR.CONFIRM_DIALOG.MESSAGE')),
        confirmText: this.translate.instant(marker('COMMON.DELETE')),
        cancelText: this.translate.instant(marker('COMMON.CANCEL')),
        transparentBackdrop: true,
      })
      .subscribe((value) => {
        if (value) {
          this.signFields = this.signFields.filter(
            (currentSignField) => currentSignField.id !== signField.id
          );
        }
      });
  }

  onChangeSignFieldType(event: IChangeSignFieldTypeEvent) {
    event.signField.type = event.type;
  }

  onChangeSignFieldPriority(event: IChangeSignFieldPriorityEvent): void {
    event.signField.priority = event.priority;
  }

  onFinishSignFieldEdit(signField: IClientSignField) {
    if (this.activeSignField.id === signField.id) {
      this.deactivateSignField();
    }
  }
}
